import { useState, useEffect, useCallback, useRef } from 'react'

const API = import.meta.env.VITE_API_URL || ''

// sort === 'hidden': lädt stattdessen die lokal ausgeblendeten Geständnisse per ID.
// Die IDs laufen über eine Ref, damit Ein-/Ausblenden keinen Neuladen auslöst.
// slug: lädt nur diesen einen Post (Einzelansicht /p/<slug>).
export function useConfessions(sort, category, hiddenIds = [], slug = null) {
  const [confessions, setConfessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const hiddenRef = useRef(hiddenIds)
  hiddenRef.current = hiddenIds

  const fetchConfessions = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (slug) {
        const res = await fetch(`${API}/api/confessions/${encodeURIComponent(slug)}`)
        if (res.status === 404) throw new Error('This confession no longer exists.')
        if (!res.ok) throw new Error('Could not load this confession.')
        setConfessions([await res.json()])
        return
      }
      if (sort === 'hidden') {
        // Abgelaufene Geständnisse liefern 404 und fallen einfach raus
        const results = await Promise.all(hiddenRef.current.map(async id => {
          try {
            const res = await fetch(`${API}/api/confessions/${id}`)
            return res.ok ? res.json() : null
          } catch {
            return null
          }
        }))
        setConfessions(results.filter(c => c && (!category || c.category === category)))
        return
      }
      const params = new URLSearchParams({ sort, limit: '50' })
      if (category) params.set('category', category)
      const res = await fetch(`${API}/api/confessions?${params}`)
      if (!res.ok) throw new Error('Feed konnte nicht geladen werden.')
      const data = await res.json()
      setConfessions(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [sort, category, slug])

  useEffect(() => { fetchConfessions() }, [fetchConfessions])

  const vote = useCallback(async (id, voteType) => {
    try {
      const res = await fetch(`${API}/api/confessions/${id}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vote_type: voteType }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Vote fehlgeschlagen.')
      }
      const updated = await res.json()
      setConfessions(prev =>
        prev.map(c => c.id === id ? { ...c, karma_score: updated.karma_score } : c)
      )
    } catch (err) {
      console.error(err.message)
    }
  }, [])

  // Absolve / Guilty – gleiches Urteil nochmal nimmt es zurück
  const judge = useCallback(async (id, verdict) => {
    try {
      const res = await fetch(`${API}/api/confessions/${id}/verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Verdict failed.')
      setConfessions(prev =>
        prev.map(c => c.id === id
          ? { ...c, absolve_count: data.absolve_count, guilty_count: data.guilty_count, my_verdict: data.my_verdict }
          : c)
      )
    } catch (err) {
      console.error(err.message)
    }
  }, [])

  // "I'm praying for you" – Toggle
  const pray = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/confessions/${id}/pray`, { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Prayer failed.')
      setConfessions(prev =>
        prev.map(c => c.id === id
          ? { ...c, prayer_count: data.prayer_count, my_prayer: data.my_prayer }
          : c)
      )
    } catch (err) {
      console.error(err.message)
    }
  }, [])

  // Lädt ein einzelnes Geständnis nach und stellt es oben an, falls es nicht
  // im aktuellen Feed ist (z. B. älter oder andere Kategorie). Gibt false zurück,
  // wenn es nicht mehr existiert.
  const ensure = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/confessions/${id}`)
      if (!res.ok) return false
      const confession = await res.json()
      setConfessions(prev =>
        prev.some(c => c.id === id)
          ? prev.map(c => c.id === id ? confession : c)
          : [confession, ...prev]
      )
      return true
    } catch {
      return false
    }
  }, [])

  const report = useCallback(async (id, reason) => {
    const res = await fetch(`${API}/api/confessions/${id}/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Melden fehlgeschlagen.')
    }
    return res.json()
  }, [])

  return { confessions, loading, error, vote, judge, pray, report, ensure, refresh: fetchConfessions }
}
